import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RiDraggable, RiDeleteBinLine, RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';
import { FIELD_TYPES, COLUMN_WIDTHS } from './constants';
import { getFieldTypeInfo, fieldTypeLabel } from './utils';
import MultiLangInput from '../../components/MultiLangInput/MultiLangInput';
import OptionsEditor from './OptionsEditor';
import { useState } from 'react';

export default function TableColumnRow({ col, sectionId, onUpdate, onRemove, errors, isDragOverlay }) {
  const { t, i18n } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const typeInfo = getFieldTypeInfo(col.type);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.id,
    data: { type: 'tableCol', sectionId, colId: col.id },
    disabled: !!isDragOverlay,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const update = (key, val) => onUpdate(sectionId, col.id, { [key]: val });
  const Icon = typeInfo.icon;

  return (
    <div
      ref={setNodeRef}
      style={isDragOverlay ? {} : style}
      className={`fm-col-row ${isDragging ? 'fm-col-row--dragging' : ''}`}
    >
      <span
        className="fm-drag-handle"
        {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
        title={t('fieldsManager.tableColumnRow.dragToReorder')}
      >
        <RiDraggable />
      </span>

      {/* Type icon badge */}
      <span
        style={{
          width: 28, height: 28, borderRadius: 7,
          background: typeInfo.bg, color: typeInfo.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, flexShrink: 0,
        }}
        title={fieldTypeLabel(t, typeInfo)}
      >
        {Icon ? <Icon /> : typeInfo.badge}
      </span>

      <div className="fm-col-row__fields">
        <div className="fm-col-row__top">
          {/* Header input */}
          <div className="fm-col-row__header-wrap">
            <MultiLangInput
              value={col.header}
              onChange={(v) => update('header', v)}
              primaryLang={i18n.language}
              placeholder={t('fieldsManager.tableColumnRow.columnNamePlaceholder')}
              className="fm-ml fm-ml--col"
              inputClassName={`fm-input fm-input--sm ${errors[`col-${col.id}-header`] ? 'fm-input--error' : ''}`}
            />
            {errors[`col-${col.id}-header`] && (
              <span className="fm-error-text">{errors[`col-${col.id}-header`]}</span>
            )}
          </div>

          {/* Type selector */}
          <select
            className="fm-select fm-select--sm"
            value={col.type}
            onChange={(e) => {
              update('type', e.target.value);
              if (e.target.value !== 'select') setShowOptions(false);
            }}
            style={{ color: typeInfo.color, fontWeight: 600, borderColor: typeInfo.color + '40' }}
            title={t('fieldsManager.tableColumnRow.dataType')}
          >
            {FIELD_TYPES.map((ft) => (
              <option key={ft.type} value={ft.type}>{fieldTypeLabel(t, ft)}</option>
            ))}
          </select>

          {/* Width selector */}
          <select
            className="fm-select fm-select--sm fm-select--width"
            value={col.width}
            onChange={(e) => update('width', e.target.value)}
            title={t('fieldsManager.tableColumnRow.width')}
          >
            {COLUMN_WIDTHS.map((w) => (
              <option key={w} value={w}>{w.charAt(0).toUpperCase() + w.slice(1)}</option>
            ))}
          </select>

          {/* Required toggle */}
          <label className="fm-toggle-label" title={t('fieldsManager.tableColumnRow.markRequired')}>
            <input
              type="checkbox"
              className="fm-toggle"
              checked={col.required}
              onChange={(e) => update('required', e.target.checked)}
            />
            <span className="fm-toggle-text">{t('fieldsManager.tableColumnRow.required')}</span>
          </label>

          {/* Options toggle for select */}
          {col.type === 'select' && (
            <button
              className="fm-icon-btn fm-icon-btn--ghost"
              onClick={() => setShowOptions((v) => !v)}
              title={t('fieldsManager.tableColumnRow.manageOptions')}
              style={{ color: showOptions ? '#6366f1' : undefined }}
            >
              {showOptions ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
            </button>
          )}

          {/* Delete */}
          <button
            className="fm-icon-btn fm-icon-btn--danger"
            onClick={() => onRemove(sectionId, col.id)}
            title={t('fieldsManager.tableColumnRow.remove')}
          >
            <RiDeleteBinLine />
          </button>
        </div>

        {/* Options editor for select columns */}
        {col.type === 'select' && showOptions && (
          <OptionsEditor
            options={col.options}
            onChange={(opts) => update('options', opts)}
            error={errors[`col-${col.id}-options`]}
          />
        )}
      </div>
    </div>
  );
}
